import React, { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { ResumeBuilderModal } from "@/components/ResumeBuilderModal";
import { SamplePicker } from "@/components/SamplePicker";

/**
 * PresetJobPicker
 * -----------------------------------------------------------------------
 * Reusable Role / Company dropdowns + auto-filled Job Description textarea,
 * populated from the user's prior Resume Alignment / Interview Practice
 * analyses (`GET /api/resume/analyses`).
 *
 * Behavior:
 *   - Each field starts in "preset" mode with a dropdown of past values;
 *     the user can switch to "custom" mode and enter any new role/company.
 *   - When the current (role, company) pair matches a prior analysis, the
 *     JD textarea auto-fills with that analysis's job_description and shows
 *     an "Auto-filled" indicator.
 *   - If the user manually edits the JD, auto-fill is suppressed until they
 *     click "Restore auto-filled JD" (visible while a match still exists).
 *   - Users with 0 prior analyses see the fields default to custom mode
 *     (identical to a plain text-input experience).
 *
 * Controlled component: parent owns `role`, `company`, `jd` state.
 * The testidPrefix drives all data-testids: `${prefix}-role`, `${prefix}-role-select`, etc.
 */
export default function PresetJobPicker({
  role,
  setRole,
  company,
  setCompany,
  jd,
  setJd,
  onResumeChange,
  testidPrefix,
  rolePlaceholder = "e.g. Senior Product Manager",
  companyPlaceholder = "e.g. Stripe",
  jdRows = 8,
  className = "",
  showNoDocsRow = true,
}) {
  const [analyses, setAnalyses] = useState([]);
  const [analysesLoading, setAnalysesLoading] = useState(true);
  const [roleMode, setRoleMode] = useState("preset");
  const [companyMode, setCompanyMode] = useState("preset");
  const [jdManuallyEdited, setJdManuallyEdited] = useState(false);
  const [resumes, setResumes] = useState([]);
  const [showBuilder, setShowBuilder] = useState(false);
  const [showSamples, setShowSamples] = useState(false);
  const [jdBusy, setJdBusy] = useState(false);
  const navigate = useNavigate();

  // Load analyses + resumes once on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [aRes, rRes] = await Promise.all([
          api.get("/resume/analyses").catch(() => ({ data: [] })),
          api.get("/resume/list").catch(() => ({ data: [] })),
        ]);
        if (!cancelled) {
          setAnalyses(Array.isArray(aRes.data) ? aRes.data : []);
          setResumes(Array.isArray(rRes.data) ? rRes.data : []);
        }
      } finally {
        if (!cancelled) setAnalysesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Case-insensitive de-dupe - collapse `country director` and
  // `Country Director` into a single option, preferring Title Case (with
  // capitals) as the display form so the dropdown looks polished.
  const _dedupe = (arr) => {
    const byKey = new Map();
    for (const raw of arr) {
      const clean = (raw || "").trim();
      if (!clean) continue;
      const key = clean.toLowerCase();
      const existing = byKey.get(key);
      if (!existing) {
        byKey.set(key, clean);
      } else {
        // Prefer the variant that has more capital letters (usually the
        // proper Title-Case form the user typed later).
        const caps = (s) => (s.match(/[A-Z]/g) || []).length;
        if (caps(clean) > caps(existing)) byKey.set(key, clean);
      }
    }
    return Array.from(byKey.values());
  };
  const uniqueRoles = _dedupe(analyses.map((a) => a.job_title));
  const uniqueCompanies = _dedupe(analyses.map((a) => a.company));

  // If the user has no prior analyses, default both fields to custom mode.
  useEffect(() => {
    if (!analysesLoading && analyses.length === 0) {
      setRoleMode("custom");
      setCompanyMode("custom");
    }
  }, [analysesLoading, analyses.length]);

  const matchingAnalysis = analyses.find(
    (a) => (a.job_title || "").trim() === (role || "").trim()
      && (a.company || "").trim() === (company || "").trim()
  );

  // Effective resume the AI will use to personalize questions:
  //   1) When a matching analysis exists → the resume that was analyzed against that JD
  //   2) Fallback → the user's most recently uploaded resume
  //   3) None → the user has no resumes at all
  const fallbackResume = resumes.length > 0 ? resumes[0] : null;
  const effectiveResume =
    (matchingAnalysis
      ? resumes.find((r) => r.resume_id === matchingAnalysis.resume_id)
      : null)
    || fallbackResume
    || null;

  // Notify parent whenever the effective resume changes (so it can be sent in the start payload).
  useEffect(() => {
    if (onResumeChange) onResumeChange(effectiveResume);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveResume?.resume_id]);

  // Auto-paste JD when the (role, company) pair matches a previous analysis.
  useEffect(() => {
    if (jdManuallyEdited) return;
    if (matchingAnalysis && matchingAnalysis.job_description) {
      setJd(matchingAnalysis.job_description);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, company, analyses]);

  // Auto-detect role + company from a freshly-pasted JD. Only runs when at
  // least one of the fields is empty AND the JD is long enough to yield a
  // meaningful match. Debounced so we don't call while the user is still
  // typing. Never overwrites what the user has already filled in.
  const lastParsedJDRef = React.useRef("");
  useEffect(() => {
    const text = (jd || "").trim();
    if (text.length < 60) return undefined;
    if (role && company) return undefined;
    if (lastParsedJDRef.current === text) return undefined;
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/resume/parse-jd", { job_description: text });
        if (cancelled) return;
        lastParsedJDRef.current = text;
        if (!role && data?.job_title) setRole(data.job_title);
        if (!company && data?.company) setCompany(data.company);
      } catch {
        // Best-effort - silent on failure.
      }
    }, 600);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [jd]);

  return (
    <>
      {/* No-documents helpers */}
      {showNoDocsRow && (
      <div className="flex flex-wrap items-center gap-2 -mb-1" data-testid={`${testidPrefix}-no-docs-row`}>
        <span className="text-[10px] uppercase tracking-widest text-zinc-600">No documents?</span>
        <button type="button" onClick={() => setShowBuilder(true)} data-testid={`${testidPrefix}-ai-resume-btn`}
          className="text-[11px] inline-flex items-center gap-1 border border-amber-500/40 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 rounded-full px-2.5 py-1">
          <Sparkles className="h-3 w-3" /> Create resume with AI
        </button>
        <button type="button" disabled={jdBusy || !(role || "").trim()} data-testid={`${testidPrefix}-ai-jd-btn`}
          onClick={async () => {
            setJdBusy(true);
            try {
              const { data } = await api.post("/resume/builder/jd", { job_title: role, company: company || null });
              setJd(data.job_description || "");
              setJdManuallyEdited(true);
            } catch { /* ignore */ } finally { setJdBusy(false); }
          }}
          className="text-[11px] inline-flex items-center gap-1 border border-zinc-700 hover:border-zinc-500 text-zinc-300 rounded-full px-2.5 py-1 disabled:opacity-40">
          {jdBusy ? "Writing JD…" : "Create JD with AI"}
        </button>
        <button type="button" onClick={() => setShowSamples(true)} data-testid={`${testidPrefix}-sample-btn`}
          className="text-[11px] inline-flex items-center gap-1 border border-zinc-700 hover:border-zinc-500 text-zinc-300 rounded-full px-2.5 py-1">
          Try a sample
        </button>
      </div>
      )}
      <ResumeBuilderModal
        open={showBuilder}
        onClose={() => setShowBuilder(false)}
        onUse={async (data) => {
          try {
            const { data: list } = await api.get("/resume/list");
            setResumes(Array.isArray(list) ? list : []);
          } catch { /* ignore */ }
          if (onResumeChange) onResumeChange({ resume_id: data.resume_id, name: data.name });
        }}
      />
      <SamplePicker
        open={showSamples}
        onClose={() => setShowSamples(false)}
        onStartInterview={(sampleId) => navigate(`/app/live?sample=${sampleId}`)}
      />
      {/* Role */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Role</div>
          {uniqueRoles.length > 0 && (
            <ModeToggle
              mode={roleMode}
              setMode={setRoleMode}
              backLabel="Use a previous role"
              testid={`${testidPrefix}-role-mode-toggle`}
            />
          )}
        </div>
        {roleMode === "preset" && uniqueRoles.length > 0 ? (
          <select
            value={role || ""}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "__custom__") {
                setRoleMode("custom");
                setRole("");
                return;
              }
              setRole(v);
            }}
            data-testid={`${testidPrefix}-role-select`}
            className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-50 focus:outline-none focus:border-zinc-700"
          >
            <option value="">Select a previous role…</option>
            {uniqueRoles.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
            <option value="__custom__">+ Enter a new role…</option>
          </select>
        ) : (
          <input
            value={role || ""}
            onChange={(e) => setRole(e.target.value)}
            placeholder={rolePlaceholder}
            data-testid={`${testidPrefix}-role`}
            className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-50 placeholder:text-zinc-600 focus:outline-none focus:border-zinc-700"
          />
        )}
      </div>

      {/* Company */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Company (optional)</div>
          {uniqueCompanies.length > 0 && (
            <ModeToggle
              mode={companyMode}
              setMode={setCompanyMode}
              backLabel="Use a previous company"
              testid={`${testidPrefix}-company-mode-toggle`}
            />
          )}
        </div>
        {companyMode === "preset" && uniqueCompanies.length > 0 ? (
          <select
            value={company || ""}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "__custom__") {
                setCompanyMode("custom");
                setCompany("");
                return;
              }
              setCompany(v);
            }}
            data-testid={`${testidPrefix}-company-select`}
            className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-50 focus:outline-none focus:border-zinc-700"
          >
            <option value="">Select a previous company…</option>
            {uniqueCompanies.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
            <option value="__custom__">+ Enter a new company…</option>
          </select>
        ) : (
          <input
            value={company || ""}
            onChange={(e) => setCompany(e.target.value)}
            placeholder={companyPlaceholder}
            data-testid={`${testidPrefix}-company`}
            className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-50 placeholder:text-zinc-600 focus:outline-none focus:border-zinc-700"
          />
        )}
      </div>

      {/* JD */}
      <div className={className}>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Job description</div>
          <div className="flex items-center gap-2">
            {effectiveResume && (
              <div
                data-testid={`${testidPrefix}-resume-chip`}
                title="The AI will personalize questions using this resume"
                className="text-[10px] uppercase tracking-widest text-emerald-300 border border-emerald-500/25 bg-emerald-500/[0.06] rounded-full px-2 py-0.5 inline-flex items-center gap-1.5 max-w-[220px]"
              >
                <Sparkles className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">
                  Personalized to <span className="font-semibold">{effectiveResume.filename || "your resume"}</span>
                </span>
              </div>
            )}
            {matchingAnalysis && !jdManuallyEdited && (
              <div
                data-testid={`${testidPrefix}-jd-auto-note`}
                className="text-[10px] uppercase tracking-widest text-emerald-400 inline-flex items-center gap-1.5"
              >
                <Sparkles className="h-3 w-3" /> Auto-filled from previous analysis
              </div>
            )}
          </div>
        </div>
        <textarea
          value={jd || ""}
          onChange={(e) => {
            setJd(e.target.value);
            setJdManuallyEdited(true);
          }}
          rows={jdRows}
          placeholder="Paste the JD here…"
          data-testid={`${testidPrefix}-jd`}
          className="w-full bg-zinc-950 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-zinc-50 placeholder:text-zinc-600 focus:outline-none focus:border-zinc-700"
        />
        {matchingAnalysis && jdManuallyEdited && (
          <button
            type="button"
            onClick={() => {
              setJd(matchingAnalysis.job_description || "");
              setJdManuallyEdited(false);
            }}
            data-testid={`${testidPrefix}-jd-reset`}
            className="mt-2 text-[10px] uppercase tracking-widest text-zinc-500 hover:text-emerald-400 transition-colors"
          >
            ← Restore auto-filled JD
          </button>
        )}
      </div>
    </>
  );
}

function ModeToggle({ mode, setMode, backLabel, testid }) {
  return (
    <button
      type="button"
      onClick={() => setMode(mode === "preset" ? "custom" : "preset")}
      data-testid={testid}
      className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-amber-400 transition-colors"
    >
      {mode === "preset" ? "+ Custom" : `← ${backLabel}`}
    </button>
  );
}
